import React, { useEffect } from "react";

const DEFAULT_OG_IMAGE =
  "/images/assets/axy5zhmj_green_wordmark.png";

const RH_ATTR = "data-os-seo";

/**
 * Seo — gestisce <head> via useEffect + DOM diretta.
 *
 * Tutti i tag iniettati hanno l'attributo data-os-seo="1" così possiamo
 * rimuoverli/ricrearli quando le props cambiano senza toccare i tag statici
 * presenti in public/index.html.
 *
 * Props:
 *  - title, description, path  → tag base + canonical
 *  - noindex (bool)            → robots
 *  - image (string)            → URL/path della og:image specifica
 *  - jsonLd (object | array)   → uno o più oggetti JSON-LD schema.org
 */
const Seo = ({
  title,
  description,
  path = "/",
  noindex = false,
  image,
  jsonLd,
}) => {
  useEffect(() => {
    const head = document.head;
    if (!head) return;

    const url = `https://www.weareonsports.com${path}`;
    const ogImage = image
      ? image.startsWith("http")
        ? image
        : `https://www.weareonsports.com${image}`
      : DEFAULT_OG_IMAGE;

    // Rimuovi i tag iniettati precedentemente
    head.querySelectorAll(`[${RH_ATTR}="1"]`).forEach((n) => n.remove());

    // <html lang>
    document.documentElement.setAttribute("lang", "it");

    // <title> (replace, mai duplicato)
    if (title) document.title = title;

    // helper: rimuove eventuali tag statici con la stessa "chiave"
    // poi inserisce il nuovo tag iniettato.
    const setMeta = (selector, attrs) => {
      // rimuovi duplicati statici per evitare conflitti SEO
      head.querySelectorAll(selector).forEach((n) => n.remove());
      const el = document.createElement("meta");
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      el.setAttribute(RH_ATTR, "1");
      head.appendChild(el);
    };

    const setLink = (selector, attrs) => {
      head.querySelectorAll(selector).forEach((n) => n.remove());
      const el = document.createElement("link");
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      el.setAttribute(RH_ATTR, "1");
      head.appendChild(el);
    };

    if (description) {
      setMeta('meta[name="description"]', {
        name: "description",
        content: description,
      });
    }

    setMeta('meta[name="robots"]', {
      name: "robots",
      content: noindex ? "noindex, follow" : "index, follow",
    });

    setLink('link[rel="canonical"]', { rel: "canonical", href: url });

    // GEO targeting
    setMeta('meta[name="geo.region"]', { name: "geo.region", content: "IT-MI" });
    setMeta('meta[name="geo.placename"]', { name: "geo.placename", content: "Milano" });
    setMeta('meta[name="geo.position"]', { name: "geo.position", content: "45.4951;9.1697" });
    setMeta('meta[name="ICBM"]', { name: "ICBM", content: "45.4951, 9.1697" });

    // Open Graph
    setMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    if (title) setMeta('meta[property="og:title"]', { property: "og:title", content: title });
    if (description)
      setMeta('meta[property="og:description"]', { property: "og:description", content: description });
    setMeta('meta[property="og:url"]', { property: "og:url", content: url });
    setMeta('meta[property="og:image"]', { property: "og:image", content: ogImage });
    setMeta('meta[property="og:locale"]', { property: "og:locale", content: "it_IT" });
    setMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "OnSports" });

    // Twitter
    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    if (title) setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    if (description)
      setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: ogImage });

    // JSON-LD
    const schemas = jsonLd
      ? Array.isArray(jsonLd)
        ? jsonLd.filter(Boolean)
        : [jsonLd]
      : [];
    schemas.forEach((schema) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute(RH_ATTR, "1");
      s.textContent = JSON.stringify(schema);
      head.appendChild(s);
    });

    return () => {
      head.querySelectorAll(`[${RH_ATTR}="1"]`).forEach((n) => n.remove());
    };
  }, [title, description, path, noindex, image, JSON.stringify(jsonLd)]);

  return null;
};

export default Seo;
